import { useState, useEffect } from "react";
import { usersApi } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Loader2, Download, Search, Copy, RefreshCw, Key, CheckCircle } from "lucide-react";

const CredentialsList = () => {
  const [loading, setLoading] = useState(true);
  const [credentials, setCredentials] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [copiedId, setCopiedId] = useState(null);
  const [regenerating, setRegenerating] = useState(null);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [credRes, usersRes] = await Promise.all([
        usersApi.getCredentials(),
        usersApi.getAll(),
      ]);
      setCredentials(credRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      toast.error("Failed to load credentials");
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
    toast.success("Copied to clipboard");
  };

  const handleRegenerate = async (userId, userName) => {
    setRegenerating(userId);
    try {
      const res = await usersApi.regeneratePassword(userId);
      toast.success(`New password for ${userName}: ${res.data.password}`);
      loadData();
    } catch (error) {
      toast.error("Failed to regenerate password");
    } finally {
      setRegenerating(null);
    }
  };

  const handleDownloadCSV = () => {
    const csvContent = [
      ["Name", "Email", "Password", "Position"].join(","),
      ...filteredCredentials.map((c) =>
        [c.name, c.email, c.password, c.position].join(",")
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `smj-credentials-${new Date().toISOString().split("T")[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
    toast.success("Credentials downloaded");
  };

  const filteredCredentials = credentials
    .filter(
      (c) =>
        c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.email.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => a.name.localeCompare(b.name));

  const getUserById = (email) => users.find((u) => u.email === email);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="loading-spinner">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="credentials-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Employee Credentials</h1>
          <p className="text-muted-foreground mt-1">
            View and manage temporary passwords for employees
          </p>
        </div>
        <Button
          onClick={handleDownloadCSV}
          className="gap-2 bg-blue-600 hover:bg-blue-700"
          data-testid="download-csv-btn"
        >
          <Download size={18} />
          Download CSV
        </Button>
      </div>

      {/* Info card */}
      <Card className="bg-amber-500/10 border-amber-200">
        <CardContent className="pt-6">
          <div className="flex gap-4">
            <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center flex-shrink-0">
              <Key className="text-amber-500" size={20} />
            </div>
            <div>
              <h3 className="font-medium text-foreground mb-1">How to share credentials</h3>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Copy individual passwords or download the full list as CSV</li>
                <li>• Share passwords securely with each employee (in person, phone, etc.)</li>
                <li>• Employees will be prompted to set their own password on first login</li>
                <li>• Use "Regenerate" if someone forgets their password</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Search */}
      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <CardTitle>Credentials List ({filteredCredentials.length})</CardTitle>
            <div className="relative">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                size={18}
              />
              <Input
                placeholder="Search employees..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-full md:w-64"
                data-testid="search-credentials"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Table data-testid="credentials-table">
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Temporary Password</TableHead>
                <TableHead>Position</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredCredentials.map((cred, index) => {
                const user = getUserById(cred.email);
                const needsReset = user?.must_reset_password;
                const hasPassword = cred.password !== "Password already changed";

                return (
                  <TableRow key={index} className="table-row-hover">
                    <TableCell className="font-medium">{cred.name}</TableCell>
                    <TableCell className="text-muted-foreground">{cred.email}</TableCell>
                    <TableCell>
                      {hasPassword ? (
                        <code className="bg-muted px-2 py-1 rounded text-sm font-mono">
                          {cred.password}
                        </code>
                      ) : (
                        <span className="text-muted-foreground italic">Already changed</span>
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground">{cred.position || "-"}</TableCell>
                    <TableCell>
                      {needsReset ? (
                        <Badge variant="outline" className="text-amber-500 border-amber-200">
                          Needs Reset
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-emerald-500 border-emerald-200">
                          <CheckCircle size={12} className="mr-1" />
                          Password Set
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        {hasPassword && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleCopy(cred.password, index)}
                            className="text-muted-foreground"
                            data-testid={`copy-password-${index}`}
                          >
                            {copiedId === index ? (
                              <CheckCircle size={16} className="text-emerald-500" />
                            ) : (
                              <Copy size={16} />
                            )}
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRegenerate(user?.id, cred.name)}
                          disabled={regenerating === user?.id}
                          className="text-muted-foreground"
                          data-testid={`regenerate-${index}`}
                        >
                          {regenerating === user?.id ? (
                            <Loader2 size={16} className="animate-spin" />
                          ) : (
                            <RefreshCw size={16} />
                          )}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {filteredCredentials.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              No credentials found matching your search.
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CredentialsList;
